/**
 * Admin → Team Approvals full page
 * Two tabs: (1) Pending applications  (2) Send invite (skips approval)
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Send, Copy, CheckCircle2, XCircle, Loader2, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TeamApprovalsWidget from "@/components/admin/TeamApprovalsWidget";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamApprovalsPage() {
  const [tab, setTab] = useState("invite");
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [invForm, setInvForm] = useState({ invited_email: "", invited_name: "", role: "project_manager" });
  const [sending, setSending] = useState(false);

  const loadRoles = async () => {
    try {
      const r = await fetch(`${API}/api/team/roles`);
      const d = await r.json();
      setRoles(d.roles || []);
    } catch { /* ignore */ }
  };

  const loadInvites = async () => {
    setLoadingInvites(true);
    try {
      const token = localStorage.getItem("sterilemate_token");
      const res = await fetch(`${API}/api/admin/team-invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const d = await res.json();
      setInvites(d.invites || []);
    } catch { /* ignore */ }
    finally { setLoadingInvites(false); }
  };

  useEffect(() => { loadRoles(); loadInvites(); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!invForm.invited_email.trim()) return toast.error("Email is required");
    setSending(true);
    try {
      const token = localStorage.getItem("sterilemate_token");
      const res = await fetch(`${API}/api/team/invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          invited_email: invForm.invited_email.trim(),
          invited_name: invForm.invited_name.trim() || null,
          role: invForm.role,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || "Invite failed");
      toast.success(`Invite sent to ${invForm.invited_email}`);
      setInvForm({ invited_email: "", invited_name: "", role: invForm.role });
      loadInvites();
    } catch (err) {
      toast.error(err.message || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied!");
  };

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="border-b border-slate-200 flex gap-4">
        <button
          onClick={() => setTab("invite")}
          className={`pb-3 px-1 border-b-2 text-sm font-bold transition-colors ${
            tab === "invite" ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="tab-invite"
        >
          <Send className="w-4 h-4 inline mr-1.5" /> Invite &amp; Manage
        </button>
        <button
          onClick={() => setTab("pending")}
          className={`pb-3 px-1 border-b-2 text-sm font-bold transition-colors ${
            tab === "pending" ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="tab-pending"
        >
          <Users className="w-4 h-4 inline mr-1.5" /> Legacy Pending Queue
        </button>
      </div>

      {tab === "pending" && (
        <div>
          <TeamApprovalsWidget />
          <p className="mt-4 text-sm text-slate-500">
            Legacy pending applications (from before the invite-only lockdown) appear here.
            All new access must be granted via the <strong>Invite &amp; Manage</strong> tab.
          </p>
        </div>
      )}

      {tab === "invite" && (
        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          {/* Invite form */}
          <form
            onSubmit={sendInvite}
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4"
            data-testid="team-invite-form"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-teal-600" />
              <h3 className="text-lg font-bold text-slate-900">Invite a team member</h3>
            </div>
            <p className="text-xs text-slate-500 -mt-2">
              An invite link + welcome email will be sent. Invites skip the approval queue.
            </p>
            <div>
              <Label htmlFor="invited_email">Their work email *</Label>
              <Input
                id="invited_email"
                type="email"
                required
                value={invForm.invited_email}
                onChange={(e) => setInvForm({ ...invForm, invited_email: e.target.value })}
                placeholder="jane@sterilemate.com"
                data-testid="invite-email"
              />
            </div>
            <div>
              <Label htmlFor="invited_name">Their name (optional)</Label>
              <Input
                id="invited_name"
                value={invForm.invited_name}
                onChange={(e) => setInvForm({ ...invForm, invited_name: e.target.value })}
                placeholder="Jane Doe"
                data-testid="invite-name"
              />
            </div>
            <div>
              <Label htmlFor="invited_role">Assigned role *</Label>
              <select
                id="invited_role"
                value={invForm.role}
                onChange={(e) => setInvForm({ ...invForm, role: e.target.value })}
                className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white"
                data-testid="invite-role"
              >
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                {roles.find(r => r.id === invForm.role)?.description}
              </p>
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold h-10"
              data-testid="invite-submit"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" /> Send Invite</>}
            </Button>
          </form>

          {/* Recent invites list */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">Recent invites</h3>
              <p className="text-xs text-slate-500">{invites.length} total</p>
            </div>
            <div className="max-h-[560px] overflow-y-auto">
              {loadingInvites ? (
                <div className="p-8 text-center text-sm text-slate-500">Loading...</div>
              ) : invites.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No invites sent yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="invites-table">
                  <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">Recipient</th>
                      <th className="text-left px-4 py-2">Role</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {invites.map((i) => (
                      <tr key={i.code} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{i.invited_name || i.invited_email}</p>
                          <p className="text-xs text-slate-500">{i.invited_email}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-700">
                          {roles.find(r => r.id === i.role)?.label || i.role}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {i.used ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><CheckCircle2 className="w-3 h-3" /> Accepted</span>
                          ) : i.expires_at && i.expires_at < new Date().toISOString() ? (
                            <span className="inline-flex items-center gap-1 text-slate-400 font-bold"><XCircle className="w-3 h-3" /> Expired</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-700 font-bold">Pending</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {!i.used && (
                            <button
                              onClick={() => copyLink(i.invite_url)}
                              className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 hover:text-teal-900"
                              data-testid={`invite-copy-${i.code}`}
                            >
                              <Copy className="w-3 h-3" /> Copy link
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
